/**
 * MUI Distance Display component
 * 
 * This component shows distance to the closest device when activated
 */
import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { getIsMeter, getScale, getDrawingBox } from 'reduxModules/ducks/ui';
import { distanceToString } from 'utilities/format';

interface DistanceDisplayProps {
  mousePosition: { x: number; y: number };
  closestDevice?: {
    type: string;
    distance: number;
    id: string;
    position: { x: number; y: number };
  };
  visible: boolean;
}

/**
 * MUI Distance Display component
 * 
 * @param props - Component props
 * @returns {JSX.Element} Distance display component
 */
const MuiDistanceDisplay: React.FC<DistanceDisplayProps> = ({ mousePosition, closestDevice, visible }) => {
  const scale = useSelector(getScale);
  const isMeter = useSelector(getIsMeter);
  const { bbox } = useSelector(getDrawingBox);

  if (!visible || !closestDevice) return null;

  const distanceText = distanceToString(closestDevice.distance, isMeter);
  
  // Format device type (remove 's' from plural)
  const deviceType = closestDevice.type.endsWith('s') ? closestDevice.type.slice(0, -1) : closestDevice.type;
  
  // Special formatting for different device types
  let tooltipText;
  if (closestDevice.type === 'microphones') {
    tooltipText = `Talker distance to nearest ${deviceType}: ${distanceText}`;
  } else if (closestDevice.type === 'cameras') {
    tooltipText = `Distance to nearest ${deviceType}: ${distanceText}`;
  } else {
    tooltipText = `Distance to closest ${deviceType}: ${distanceText}`;
  }

  return (
    <DistanceTooltip
      transform={`translate(${mousePosition.x + 10}, ${mousePosition.y - 10})`}
    >
      <DistanceBackground
        width="250"
        height="40"
        rx="4"
        ry="4"
      />
      <DistanceText x="10" y="25">
        {tooltipText}
      </DistanceText>
    </DistanceTooltip>
  );
};

// Styled components using MUI styled API
const DistanceTooltip = styled('g')({
  pointerEvents: 'none',
  zIndex: 1000,
});

const DistanceBackground = styled('rect')({
  fill: '#333',
  stroke: '#666',
  strokeWidth: 1,
  opacity: 0.9,
});

const DistanceText = styled('text')({
  fill: '#fff',
  fontSize: '12px',
  fontFamily: 'Arial, sans-serif',
});


export default MuiDistanceDisplay;
